// src/components/TickerMultiSelectMenu.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Chip,
  Paper,
  Popper,
  Typography,
  ClickAwayListener,
  Checkbox,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useTheme } from "@mui/material/styles";
import {
  ControlButton,
  getControlMenuPaperSx,
  getControlTokens,
} from "../common/controls";
import {
  formatCompanyDisplayLabel,
  isPrivateCompanyIdentifier,
  matchesCompanySearch,
} from '../../utils/companyHelpers';

function TickerMultiSelectMenu({
  allTickers,
  selectedTickers,
  setSelectedTickers,
  leftBorderOnly = false,
  controlContext = 'filterBar',
  menuContext = controlContext,
  width,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [showAllChips, setShowAllChips] = useState(false);

  const theme = useTheme();
  const tokens = getControlTokens(theme, controlContext);
  const menuTokens = getControlTokens(theme, menuContext);
  const resolvedWidth = typeof width === 'number' ? `${width}px` : width;
  const numericWidth = typeof width === 'number'
    ? width
    : typeof width === 'string'
      ? Number.parseFloat(width)
      : null;
  const isCompact = numericWidth !== null && numericWidth <= 220;
  const visibleChipCount = isCompact ? 1 : 4;
  const visibleTickers = showAllChips && !isCompact
    ? selectedTickers
    : selectedTickers.slice(0, visibleChipCount);
  const hiddenTickerCount = Math.max(selectedTickers.length - visibleTickers.length, 0);
  const inputRef = useRef(null);
  const filterInputRef = useRef(null);

  // Handle opening/closing the menu
  const handleToggle = (event) => {
    const isClosing = Boolean(anchorEl);
    setAnchorEl(isClosing ? null : event.currentTarget);
    if (isClosing) {
      setShowAllChips(false);
    }
  };
  const open = Boolean(anchorEl);

  // Autofocus filter input when the menu opens
  useEffect(() => {
    if (anchorEl && filterInputRef.current) {
      setTimeout(() => filterInputRef.current?.focus(), 100);
    }
  }, [anchorEl]);

  // Ticker selection handlers
  const handleSelectTicker = (tickerObj) => {
    if (!selectedTickers.find((t) => t.ticker === tickerObj.ticker)) {
      const updated = [...selectedTickers, tickerObj].sort((a, b) =>
        a.ticker.localeCompare(b.ticker)
      );
      setSelectedTickers(updated);
    }
  };

  const handleRemoveTicker = (ticker) => {
    setSelectedTickers(selectedTickers.filter((t) => t.ticker !== ticker));
  };

  // Filter allTickers by inputValue
  const filteredOptions =
    inputValue.length > 0
      ? allTickers.filter(
          (option) => matchesCompanySearch(option, inputValue)
        )
      : [];

  // Clear all selected tickers
  const clearAll = () => {
    setSelectedTickers([]);
    setShowAllChips(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flex: resolvedWidth ? '0 0 auto' : 1,
        width: resolvedWidth,
        minWidth: resolvedWidth,
        maxWidth: resolvedWidth,
      }}
    >
      {/* The "button" showing selected tickers */}
      <Box
        data-testid="ticker-filter-control"
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          gap: "2px",
          backgroundColor: tokens.surface,
          ...(leftBorderOnly
            ? {
                borderRadius: 0,
                border: 'none',
              }
            : {
                borderRadius: 1,
                border: `1px solid ${tokens.borderColor}`,
              }
          ),
          px: 1,
          py: 0,
          cursor: "pointer",
          height: tokens.height,
          alignItems: "center",
          overflow: 'hidden',
          color:
            selectedTickers.length === 0
              ? theme.palette.text.disabled
              : "inherit",
          flex: 1,
          minWidth: 0,
          position: "relative",
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            borderColor: theme.palette.primary.main,
          },
        }}
        onClick={handleToggle}
        ref={inputRef}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            alignItems: 'center',
            overflow: 'hidden',
            pr: 3,
            flex: 1,
            minWidth: 0,
          }}
        >
          {selectedTickers.length === 0 ? (
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontSize: tokens.fontSize,
                color: theme.palette.text.secondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Filter by Company
            </Typography>
          ) : (
            visibleTickers.map((item) => (
              <Chip
                key={item.ticker}
                label={formatCompanyDisplayLabel({
                  ticker: item.ticker,
                  companyName: item.name,
                  publicStyle: 'ticker',
                })}
                size="small"
                sx={{
                  fontSize: "9px",
                  height: "18px",
                  margin: "1px",
                  maxWidth: isCompact ? 64 : 90,
                  flexShrink: 0,
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(100, 150, 220, 0.3)'
                    : 'rgba(40, 100, 180, 0.15)',
                  color: theme.palette.text.primary,
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(100, 150, 220, 0.4)'
                    : '1px solid rgba(40, 100, 180, 0.35)',
                  transition: 'all 0.15s ease',
                  '& .MuiChip-deleteIcon': {
                    fontSize: '14px',
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: theme.palette.error.main,
                    },
                  },
                  '& .MuiChip-label': {
                    px: '6px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
                onDelete={() => handleRemoveTicker(item.ticker)}
              />
            ))
          )}
          {hiddenTickerCount > 0 && (
            <Chip
              label={isCompact ? `+${hiddenTickerCount}` : `+${hiddenTickerCount} more`}
              size="small"
              onClick={isCompact
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    setShowAllChips(true);
                  }}
              sx={{
                fontSize: "9px",
                height: "18px",
                margin: "1px",
                maxWidth: isCompact ? 42 : 82,
                flexShrink: 0,
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(100, 100, 140, 0.3)'
                  : 'rgba(100, 100, 140, 0.15)',
                color: theme.palette.text.secondary,
                border: `1px solid ${theme.palette.divider}`,
                cursor: isCompact ? "default" : "pointer",
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(100, 100, 140, 0.5)'
                    : 'rgba(100, 100, 140, 0.25)',
                },
                '& .MuiChip-label': {
                  px: '6px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          )}
        </Box>
        <ArrowDropDownIcon
          sx={{
            position: "absolute",
            top: "50%",
            right: 8,
            transform: "translateY(-50%)",
            fontSize: 24,
            color: theme.palette.text.secondary,
          }}
        />
      </Box>

      {/* Popper menu */}
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
        <ClickAwayListener
          onClickAway={() => {
            setAnchorEl(null);
            setShowAllChips(false);
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 2,
              mt: 0.5,
              width: 330,
              maxWidth: 330,
              maxHeight: 500,
              overflowY: "auto",
              color: theme.palette.text.secondary,
              ...getControlMenuPaperSx(theme, menuContext),
            }}
          >
            {/* "Filter by Company" section */}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                color: theme.palette.text.secondary,
                mb: 1.5,
              }}
            >
              Filter by Company
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                inputRef={filterInputRef}
                variant="outlined"
                size="small"
                autoComplete="off"
                placeholder="Search companies..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                InputProps={{
                  sx: {
                    backgroundColor: theme.tableHeader.highlight,
                    fontSize: menuTokens.fontSize,
                    color: theme.palette.text.primary,
                    height: menuTokens.height,
                    borderRadius: `${menuTokens.radius}px`,
                    transition: 'border-color 0.2s ease',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: '2px',
                    },
                  },
                }}
                sx={{ flexGrow: 1 }}
              />
              <ControlButton
                context={menuContext}
                onClick={clearAll}
                buttonVariant="text"
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.error.main,
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Clear All
              </ControlButton>
            </Box>

            {/* Filtered ticker options */}
            <Box mt={1}>
              {filteredOptions.map((option) => {
                const isSelected = selectedTickers.some((t) => t.ticker === option.ticker);
                return (
                  <Box
                    key={option.ticker}
                    onClick={() => {
                      if (isSelected) {
                        handleRemoveTicker(option.ticker);
                      } else {
                        handleSelectTicker(option);
                      }
                    }}
                    sx={{
                      minHeight: '26px',
                      height: '26px',
                      px: 1.5,
                      py: 0.25,
                      fontSize: 12,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: 'space-between',
                      backgroundColor: isSelected
                        ? theme.palette.mode === 'dark'
                          ? 'rgba(145, 166, 205, 0.16)'
                          : 'rgba(63, 100, 168, 0.16)'
                        : 'transparent',
                      borderLeft: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : '2px solid transparent',
                      transition: 'background-color 0.1s ease',
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '12px',
                        color: theme.palette.text.secondary,
                        lineHeight: 1.2,
                      }}
                    >
                      {isPrivateCompanyIdentifier(option.ticker) ? (
                        formatCompanyDisplayLabel({ ticker: option.ticker, companyName: option.name })
                      ) : (
                        <><strong>{option.ticker}</strong> - {option.name}</>
                      )}
                    </Typography>
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{
                        p: 0,
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}

export default TickerMultiSelectMenu;
